import { useState, useEffect } from "react";
import { http, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Download } from "lucide-react";
import { Modal } from "./AdminShared";

export default function MessagesPanel() {
  const [tab, setTab] = useState("contact");
  const [msgs, setMsgs] = useState([]);
  const [subs, setSubs] = useState([]);
  const [openMsg, setOpenMsg] = useState(null);

  const loadAll = async () => {
    const [a, b] = await Promise.all([
      http.get("/admin/contact-messages"),
      http.get("/admin/newsletter"),
    ]);
    setMsgs(a.data);
    setSubs(b.data);
  };
  useEffect(() => { loadAll().catch(() => {}); }, []);

  const downloadCsv = (kind) => {
    const url = `${API_BASE}/api/admin/${kind === "contact" ? "contact-messages" : "newsletter"}.csv`;
    const token = localStorage.getItem("stc_token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = kind === "contact" ? "stc-contact-messages.csv" : "stc-newsletter-subscribers.csv";
        a.click();
      })
      .catch(() => toast.error("Download failed."));
  };

  const markRead = async (m) => {
    await http.patch(`/admin/contact-messages/${m.id}`, { status: "read" });
    loadAll();
  };
  const deleteMsg = async (m) => {
    if (!confirm("Delete this message?")) return;
    await http.delete(`/admin/contact-messages/${m.id}`);
    setOpenMsg(null);
    loadAll();
  };
  const deleteSub = async (s) => {
    if (!confirm(`Remove ${s.email} from the newsletter?`)) return;
    await http.delete(`/admin/newsletter/${s.id}`);
    loadAll();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 border-b border-white/10 pb-3">
        <button onClick={() => setTab("contact")} className={`font-ui text-[11px] tracking-[0.22em] uppercase px-3 py-1.5 border ${tab === "contact" ? "bg-[#C9A055] text-black border-[#C9A055]" : "border-white/15 text-[#F4F1EA] hover:border-[#C9A055]"}`} data-testid="messages-tab-contact">Contact ({msgs.length})</button>
        <button onClick={() => setTab("newsletter")} className={`font-ui text-[11px] tracking-[0.22em] uppercase px-3 py-1.5 border ${tab === "newsletter" ? "bg-[#C9A055] text-black border-[#C9A055]" : "border-white/15 text-[#F4F1EA] hover:border-[#C9A055]"}`} data-testid="messages-tab-newsletter">Newsletter ({subs.length})</button>
        <button onClick={() => downloadCsv(tab)} className="btn-outline text-xs ml-auto" data-testid="messages-csv-btn"><Download size={12}/> Export CSV</button>
      </div>

      {tab === "contact" ? (
        <div className="border border-white/10 bg-[#0a0a0a] divide-y divide-white/5" data-testid="messages-contact-list">
          {msgs.length === 0 ? <div className="p-6 text-[#8a857d] font-body">No messages yet.</div> :
            msgs.map(m => (
              <div key={m.id} onClick={() => setOpenMsg(m)} className="px-4 py-3 cursor-pointer hover:bg-white/5 flex items-center justify-between gap-3" data-testid={`msg-row-${m.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {m.status !== "read" && <span className="w-2 h-2 rounded-full bg-[#C9A055]"/>}
                    <div className="font-display text-[#F4F1EA] text-base truncate">{m.name || "(no name)"} <span className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">· {m.email}</span></div>
                  </div>
                  <div className="font-body text-[13px] text-[#B5B0A7] truncate">{m.message}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">{new Date(m.created_at).toLocaleDateString()}</div>
                  {m.order_number && <div className="font-mono text-[10px] text-[#C9A055]">{m.order_number}</div>}
                </div>
              </div>
            ))
          }
        </div>
      ) : (
        <div className="border border-white/10 bg-[#0a0a0a]" data-testid="messages-newsletter-list">
          {subs.length === 0 ? <div className="p-6 text-[#8a857d] font-body">No subscribers yet.</div> :
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left">
                  <th className="px-4 py-3 font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">Email</th>
                  <th className="px-4 py-3 font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">Status</th>
                  <th className="px-4 py-3 font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">Signed up</th>
                  <th className="px-4 py-3 w-24 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {subs.map(s => (
                  <tr key={s.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="px-4 py-2.5 text-[#F4F1EA]">{s.email}</td>
                    <td className="px-4 py-2.5">
                      {s.unsubscribed_at ? (
                        <span className="inline-block px-2 py-0.5 border border-[#d97a7a]/40 text-[#d97a7a] font-ui text-[9px] tracking-[0.22em]">UNSUBSCRIBED</span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 border border-[#5fc377]/40 text-[#5fc377] font-ui text-[9px] tracking-[0.22em]">ACTIVE</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-[#B5B0A7] text-xs">{new Date(s.created_at).toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="inline-flex gap-1">
                        {s.unsubscribed_at && (
                          <button
                            onClick={async () => {
                              await http.post(`/admin/newsletter/${s.id}/resubscribe`);
                              toast.success("Re-subscribed");
                              loadAll();
                            }}
                            className="px-2 py-1 border border-[#5fc377]/40 text-[#5fc377] hover:bg-[#5fc377] hover:text-black font-ui text-[9px] tracking-[0.22em]"
                            data-testid={`news-resub-${s.id}`}
                          >RE-SUB</button>
                        )}
                        <button onClick={() => deleteSub(s)} className="text-red-400 hover:text-red-300 px-2" data-testid={`news-delete-${s.id}`}><Trash2 size={13}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          }
        </div>
      )}

      {openMsg && (
        <Modal title={openMsg.name || "Contact Message"} onClose={() => setOpenMsg(null)}>
          <div className="space-y-3 text-sm">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">From</div><div className="text-[#F4F1EA]">{openMsg.email}</div></div>
              <div><div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">Received</div><div className="text-[#B5B0A7]">{new Date(openMsg.created_at).toLocaleString()}</div></div>
              {openMsg.order_number && <div><div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">Order Ref</div><div className="font-mono text-[#C9A055]">{openMsg.order_number}</div></div>}
              <div><div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">Status</div><div className="text-[#F4F1EA] capitalize">{openMsg.status || "new"}</div></div>
            </div>
            <div className="border border-white/10 p-4 bg-[#050505] whitespace-pre-line text-[#E8E4DC]">{openMsg.message}</div>
            <div className="flex justify-end gap-2 pt-3 border-t border-white/10">
              <a href={`mailto:${openMsg.email}?subject=Re:%20Simple%20Truths%20Club`} className="btn-outline text-xs" data-testid="msg-reply-btn">Reply by Email</a>
              {openMsg.status !== "read" && <button onClick={() => markRead(openMsg)} className="btn-outline text-xs" data-testid="msg-markread-btn">Mark as Read</button>}
              <button onClick={() => deleteMsg(openMsg)} className="btn-outline text-xs border-red-700 text-red-400" data-testid="msg-delete-btn"><Trash2 size={12}/> Delete</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
